import {
  computed,
  onScopeDispose,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  toValue,
} from 'vue'

import { insightActionsApi } from '../api/InsightActionsApi'
import { LoadPreviewFlow } from '../flows/LoadPreviewFlow'
import { PollRunFlow } from '../flows/PollRunFlow'
import { previewKey, useInsightActionStore } from '../stores/insightActionStore'

import { useInsightActionGate, type InsightActionGate } from './useInsightActionGate'
import { useInsightActionScope } from './useInsightActionScope'

const READINESS_EVENT = 'onboarding_task_update'

export const READINESS_DEBOUNCE_MS = 1500

export const WIDE_PANEL_WIDTH = '78vw'

export interface InsightActionEventBus {
  on: (name: typeof READINESS_EVENT, handler: (payload: unknown) => void) => () => void
}

let installedEventBus: InsightActionEventBus | null = null

export function registerInsightActionEventBus(bus: InsightActionEventBus): void {
  installedEventBus = bus
}

export function resetInsightActionEventBusForTest(): void {
  installedEventBus = null
}

function isReadinessDone(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false
  const row = payload as Record<string, unknown>
  return row.task_name === 'insight_action_readiness' && row.status === 'done'
}

export interface UseInsightActionSurfaceOptions {
  bus?: InsightActionEventBus | null
}

export interface UseInsightActionSurface {
  gate: ComputedRef<InsightActionGate>
  footerVisible: ComputedRef<boolean>
  panelWidthOverride: ComputedRef<string>
}

export function useInsightActionSurface(
  insightId: MaybeRefOrGetter<number | null | undefined>,
  options: UseInsightActionSurfaceOptions = {},
): UseInsightActionSurface {
  const store = useInsightActionStore()
  const scope = useInsightActionScope()
  const { mayRequestPreview, resolve } = useInsightActionGate()
  const loadPreview = new LoadPreviewFlow({ api: insightActionsApi, store })
  const pollRun = new PollRunFlow({ api: insightActionsApi, store })

  function load(force = false): void {
    const ids = scope.forInsight(toValue(insightId))
    if (!ids || !mayRequestPreview.value) return
    void loadPreview.execute(ids, { force })
  }

  watch(
    () => [toValue(insightId), scope.accountId.value, scope.workspaceId.value] as const,
    ([id, accountId, workspaceId]) => {
      if (id === null || id === undefined || accountId === null || workspaceId === null) return
      store.bindInsight(previewKey(accountId, workspaceId, id), Number(id))
      load()
      const runScope = scope.forRun()
      if (runScope && store.run && !store.isRunTerminal) {
        void pollRun.execute(runScope, store.activeKey)
      }
    },
    { immediate: true },
  )

  let warmTimer: ReturnType<typeof setTimeout> | undefined
  let unsubscribe: (() => void) | null = null

  const bus = options.bus === undefined ? installedEventBus : options.bus
  if (bus) {
    unsubscribe = bus.on(READINESS_EVENT, (payload) => {
      if (!isReadinessDone(payload)) return
      if (!store.panelOpen || !store.hasWarmingGroup) return
      clearTimeout(warmTimer)
      warmTimer = setTimeout(() => load(true), READINESS_DEBOUNCE_MS)
    })
  }

  onScopeDispose(() => {
    clearTimeout(warmTimer)
    unsubscribe?.()
  })

  const gate = computed(() =>
    resolve({
      loading: store.entry.loading,
      errorCode: store.entry.errorCode,
      executionEnabled: store.executionEnabled,
      groupCount: store.groups.length,
    }),
  )

  const footerVisible = computed(
    () => store.mode !== 'read' || ['loading', 'open'].includes(gate.value),
  )

  const panelWidthOverride = computed(() => (store.mode === 'read' ? '' : WIDE_PANEL_WIDTH))

  return { gate, footerVisible, panelWidthOverride }
}
